import React, { useEffect, useRef } from "react"
import styled from "styled-components"

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  /* fallback shown before/instead of WebGL */
  background: radial-gradient(
      120% 90% at 70% 15%,
      rgba(231, 163, 56, 0.22),
      rgba(11, 10, 8, 0) 55%
    ),
    radial-gradient(
      90% 80% at 20% 90%,
      rgba(168, 111, 31, 0.18),
      rgba(11, 10, 8, 0) 60%
    ),
    var(--color-ink);
`

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`

// Domain-warped fractal noise → flowing "liquid gold", with a soft
// warm spotlight that follows the cursor (u_mouse).
const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_speed;
uniform vec2 u_mouse;

float hash(vec2 p){ p = fract(p * vec2(123.34, 345.45)); p += dot(p, p + 34.345); return fract(p.x * p.y); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i), b = hash(i + vec2(1.0, 0.0)), c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p){
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
  return v;
}

void main(){
  vec2 uv = gl_FragCoord.xy / u_res.xy;
  float aspect = u_res.x / u_res.y;
  vec2 p = uv;
  p.x *= aspect;
  float t = u_time * u_speed;

  // cursor pulls the flow slightly + lights it up
  vec2 m = u_mouse;
  vec2 md = (uv - m);
  md.x *= aspect;
  float mdist = length(md);
  float pull = smoothstep(0.6, 0.0, mdist) * 0.18;

  vec2 q = vec2(fbm(p + vec2(0.0, t)), fbm(p + vec2(5.2, 1.3 - t)));
  vec2 r = vec2(
    fbm(p + 3.5 * q + vec2(1.7, 9.2) + 0.4 * t - pull),
    fbm(p + 3.5 * q + vec2(8.3, 2.8) - 0.4 * t + pull)
  );
  float f = fbm(p + 3.5 * r);
  float fv = clamp(f * f * 1.7, 0.0, 1.0);

  vec3 ink  = vec3(0.043, 0.039, 0.031);
  vec3 deep = vec3(0.21, 0.10, 0.02);
  vec3 gold = vec3(0.905, 0.639, 0.220);
  vec3 hot  = vec3(1.0, 0.78, 0.40);

  vec3 col = mix(ink, deep, smoothstep(0.0, 0.5, fv));
  col = mix(col, gold, smoothstep(0.5, 0.9, fv));
  col = mix(col, hot, smoothstep(0.85, 1.0, fv));
  col *= 0.92;

  // cursor spotlight — warm lift that trails the pointer
  float spot = smoothstep(0.5, 0.0, mdist);
  col += spot * spot * vec3(0.5, 0.33, 0.12) * 0.85;

  float vig = smoothstep(1.25, 0.35, length(uv - 0.5));
  col *= mix(0.5, 1.0, vig);

  gl_FragColor = vec4(col, 1.0);
}
`

function compile(gl, type, src) {
  const sh = gl.createShader(type)
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh)
    return null
  }
  return sh
}

// speed: shader flow rate. className: lets callers blend/dim it (e.g. ambient).
const ShaderBackground = ({ speed = 0.05, className }) => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const gl =
      canvas.getContext("webgl", { antialias: false, alpha: false }) ||
      canvas.getContext("experimental-webgl")
    if (!gl) return // CSS fallback background stays

    const vs = compile(gl, gl.VERTEX_SHADER, VERT)
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG)
    if (!vs || !fs) return

    const prog = gl.createProgram()
    gl.attachShader(prog, vs)
    gl.attachShader(prog, fs)
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return
    gl.useProgram(prog)

    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    )
    const loc = gl.getAttribLocation(prog, "a_pos")
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

    const uRes = gl.getUniformLocation(prog, "u_res")
    const uTime = gl.getUniformLocation(prog, "u_time")
    const uSpeed = gl.getUniformLocation(prog, "u_speed")
    const uMouse = gl.getUniformLocation(prog, "u_mouse")

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
    const resize = () => {
      const w = Math.floor(canvas.clientWidth * dpr)
      const h = Math.floor(canvas.clientHeight * dpr)
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.uniform2f(uRes, canvas.width, canvas.height)
    }
    resize()
    window.addEventListener("resize", resize)
    gl.uniform1f(uSpeed, speed)

    // cursor target (uv space, y-up) + eased current position
    const target = { x: 0.5, y: 0.55 }
    const cur = { x: 0.5, y: 0.55 }
    gl.uniform2f(uMouse, cur.x, cur.y)
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      target.x = (e.clientX - rect.left) / rect.width
      target.y = 1 - (e.clientY - rect.top) / rect.height
    }
    window.addEventListener("pointermove", onMove, { passive: true })

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const start = Date.now()
    let raf = null
    let visible = true
    const renderOnce = () => {
      gl.uniform1f(uTime, (Date.now() - start) / 1000)
      gl.uniform2f(uMouse, cur.x, cur.y)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    }
    const loop = () => {
      cur.x += (target.x - cur.x) * 0.06
      cur.y += (target.y - cur.y) * 0.06
      renderOnce()
      raf = window.requestAnimationFrame(loop)
    }
    const play = () => {
      if (raf == null && visible && !document.hidden) loop()
    }
    const pause = () => {
      if (raf != null) {
        window.cancelAnimationFrame(raf)
        raf = null
      }
    }

    let io
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          visible = entries[0].isIntersecting
          if (visible) reduce ? renderOnce() : play()
          else pause()
        },
        { threshold: 0.01 }
      )
      io.observe(canvas)
    }

    const onVis = () =>
      document.hidden ? pause() : reduce ? renderOnce() : play()
    document.addEventListener("visibilitychange", onVis)

    if (reduce) {
      gl.uniform1f(uTime, 14.0)
      renderOnce()
    } else {
      play()
    }

    return () => {
      window.removeEventListener("resize", resize)
      window.removeEventListener("pointermove", onMove)
      document.removeEventListener("visibilitychange", onVis)
      if (io) io.disconnect()
      pause()
      const ext = gl.getExtension("WEBGL_lose_context")
      if (ext) ext.loseContext()
    }
  }, [speed])

  return <Canvas ref={canvasRef} className={className} aria-hidden="true" />
}

export default ShaderBackground
